import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Injector, Input, OnChanges, OnDestroy, OnInit, Optional, Output, SimpleChanges, ViewChild } from '@angular/core';
import { ControlValueAccessor, FormControl, FormControlDirective, FormControlName, FormGroupDirective, NG_VALUE_ACCESSOR, NgControl } from '@angular/forms';
import { MatSelect } from '@angular/material/select';
import { BehaviorSubject } from 'rxjs';
import { TooltipDirective } from '@logscl/ng2-tooltip-directive';
import { arraysEqual, groupBy } from '../../helpers/array.helper';
import { SelectGroup, SelectItem, SelectItemType } from './select-item.type';
import { Selection } from './selection';
import { DEFAULT_DEBOUNCE_TIME } from '../../types/constants';

@Component({
  selector: 'shared-select-with-search',
  templateUrl: './select-with-search.component.html',
  styleUrls: ['./select-with-search.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: NG_VALUE_ACCESSOR, multi: true, useExisting: SelectWithSearchComponent }],
  standalone: false,
})
export class SelectWithSearchComponent implements ControlValueAccessor, OnInit, OnChanges, OnDestroy {
  @ViewChild(MatSelect, { static: false }) matSelect: MatSelect;

  @Input() selection: string | string[];
  @Input() items: SelectItem[] = [];
  @Input() fixedItems: SelectItem[] = [];
  @Input() multiple = false;
  @Input() iconKey: string;
  @Input() placeholder: string;
  @Input() searchPlaceholder = 'Search...';
  @Input() panelClass: string | string[] | Set<string> | Record<string, any>;
  @Input() selectClass: string | string[] | Set<string> | Record<string, any>;
  @Input() disabled = false;
  @Input() defaultValue: string | string[];
  @Input() tooltip: string;
  @Input() selectAllLabel: string;
  @Input() sortItemsByLabel = true;
  @Output() selectionChange = new EventEmitter<string | string[]>();
  @Output() selectionEnd = new EventEmitter<string | string[]>();

  get selectionModel(): Selection {
    return this._selectionModel;
  }

  set selectionModel(value: Selection) {
    this.updateSelection(value, false);
    this.notifyTouched();
    this.onChange(this.getSelectionIds());
  }

  get filter(): string {
    return this._filter;
  }

  set filter(value: string) {
    if (this.filterTimeout !== null) {
      clearTimeout(this.filterTimeout);
    }
    this.filterTimeout = window.setTimeout(() => {
      this._filter = value;
      this.filterItems();
      this.filterTimeout = null;
    }, DEFAULT_DEBOUNCE_TIME);
  }

  get isInvalid(): boolean {
    return !!(this.formControl?.invalid && this.formControl?.touched);
  }

  private static readonly SELECT_ALL_ID = '__SELECT_ALL__';

  groups$ = new BehaviorSubject<SelectGroup[]>([]);
  triggerLabel: string;

  selectAllItem: SelectItem | null = null;

  private _selectionModel: Selection;
  private _filter = '';
  private formControl: FormControl;
  private isInitialized = false;
  private filterTimeout: number | null = null;
  private onChange = (value: string | string[]): void => this.selectionChange.emit(value);
  private onTouched: () => void = null;

  constructor(
    private injector: Injector,
    private cdr: ChangeDetectorRef,
    @Optional() private tooltipDirective: TooltipDirective,
  ) {
    // hide tooltip for whole component, display it only on select element
    if (tooltipDirective) {
      this.tooltipDirective.options = { ...this.tooltipDirective.options, display: false };
    }
  }

  trackById = (index: number, item: SelectItem): any => {
    // Use a combination of index and id to handle cases where id might be falsy (including false)
    return item ? `${index}_${item.id}` : index;
  };

  get fixedItemsWithoutGroup(): SelectItem[] {
    return (this.fixedItems ?? []).filter(item => !item.group);
  }

  isFixedItemInGroup(item: SelectItem, group: SelectGroup): boolean {
    return group.fixedItems?.some(fi => fi.id === item.id) ?? false;
  }

  ngOnInit(): void {
    try {
      this.initializeFormControl();
      this.createSelectAllItem();
      this.filterItems();
      const selectionFromIds = this.getSelectionFromIds(this.selection);
      this.updateSelection(selectionFromIds, true);
      this.isInitialized = true;
    } catch (error) {
      console.error('SelectWithSearchComponent initialization failed:', error);
      // Set safe defaults to prevent hanging
      this._selectionModel = this.multiple ? [] : null;
      this.triggerLabel = '';
      this.groups$.next([]);
      this.isInitialized = true;
      this.cdr.markForCheck();
    }
  }

  private initializeFormControl(): void {
    try {
      const ngControl = this.injector.get(NgControl, null);
      if (!ngControl) {
        this.formControl = null;
        return;
      }

      if (ngControl instanceof FormControlName) {
        const formGroupDirective = this.injector.get(FormGroupDirective, null);
        this.formControl = formGroupDirective?.getControl(ngControl) ?? null;
      } else if (ngControl instanceof FormControlDirective) {
        this.formControl = ngControl.form;
      } else {
        this.formControl = null;
      }
    } catch (error) {
      console.warn('Failed to initialize form control:', error);
      this.formControl = null;
    }
  }

  private createSelectAllItem(): void {
    if (!this.selectAllLabel || !this.multiple) {
      this.selectAllItem = null;
      return;
    }

    this.selectAllItem = {
      id: SelectWithSearchComponent.SELECT_ALL_ID,
      label: this.selectAllLabel,
      type: SelectItemType.All,
      isFixed: true,
    };
  }

  ngOnDestroy(): void {
    if (this.filterTimeout !== null) {
      clearTimeout(this.filterTimeout);
      this.filterTimeout = null;
    }
    this.groups$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.isInitialized) {
      return;
    }

    try {
      if (changes.items) {
        this.createSelectAllItem();
        this.filterItems();
      }

      if (changes.selectAllLabel) {
        this.createSelectAllItem();
        this.filterItems();
      }

      if (changes.selection && !changes.selection.firstChange) {
        // Prevent infinite loops by checking if selection actually changed
        const currentIds = this.normalizeToArray(this.getSelectionIds());
        const newIds = this.normalizeToArray(this.selection);

        if (!arraysEqual(currentIds, newIds)) {
          const selectionFromIds = this.getSelectionFromIds(this.selection);
          this.updateSelection(selectionFromIds, true);
        }
      }
    } catch (error) {
      console.error('SelectWithSearchComponent ngOnChanges failed:', error);
    }
  }

  onClosed(): void {
    this.notifyTouched();
    this.selectionEnd.emit(this.getSelectionIds());
  }

  compareItems(o1: SelectItem, o2: SelectItem): boolean {
    return o1?.id === o2?.id;
  }

  resetToDefault(): void {
    const defaultSelection = this.getSelectionFromIds(this.defaultValue);
    this.updateSelection(defaultSelection, true);
    this.notifyTouched();
    this.onChange(this.getSelectionIds());
    this.selectionEnd.emit(this.getSelectionIds());
  }

  canResetToDefault(): boolean {
    if (this.defaultValue === undefined) {
      return false;
    }

    const defaultIds = this.normalizeToArray(this.defaultValue);
    const selectedIds = this.normalizeToArray(this.getSelectionIds());

    // Filter out auto-synced SelectItemType.All items from comparison
    // These items are automatically added/removed by syncFixedItems() based on regular item selection
    const selectAllTypeItemIds = this.getSelectAllTypeItems().map(item => item.id);
    const userSelectedIds = selectedIds.filter(id => !selectAllTypeItemIds.includes(id));
    const userDefaultIds = defaultIds.filter(id => !selectAllTypeItemIds.includes(id));

    return !arraysEqual(userDefaultIds, userSelectedIds);
  }

  private normalizeToArray(value: string | string[]): string[] {
    if (value == null) return [];
    return Array.isArray(value) ? value : [value];
  }

  private compareByLabel = (a: SelectItem, b: SelectItem): number => a.label?.localeCompare(b.label) ?? 0;

  private sortItems(items: SelectItem[]): SelectItem[] {
    return this.sortItemsByLabel ? items.sort(this.compareByLabel) : items;
  };

  private updateSelectTriggerLabel(): void {
    if (!Array.isArray(this.selectionModel) || this.items == null) {
      this.triggerLabel = null;
      return;
    }

    // Check for any "All" type items (including "Any tag" from fixedItems)
    const selectAllTypeItem = this.selectionModel.find(i => i.type === SelectItemType.All);
    const explicitItems = this.selectionModel.filter(i => i.type === SelectItemType.Explicit);

    // If "All" type item is selected, show it along with any Explicit items
    if (selectAllTypeItem != null) {
      const labels: string[] = [selectAllTypeItem.label];
      if (explicitItems.length > 0) {
        labels.push(...this.sortItems(explicitItems).map(item => item.label));
      }

      this.triggerLabel = labels.join(', ');
      return;
    }

    // Otherwise show all selected items (excluding "All" type items)
    const itemsInLabel = this.selectionModel.filter(i => i.type !== SelectItemType.All);
    // If no items selected, show placeholder (triggerLabel = null)
    this.triggerLabel = itemsInLabel.length > 0 ? this.sortItems(itemsInLabel).map(i => i.label).join(', ') : null;
  }

  private filterItems(): void {
    try {
      const items = this.items ?? [];
      const filterValue = this.filter.toLowerCase();
      const filteredItems = this.filter === '' ? items : items.filter(i => i?.label?.toLowerCase().includes(filterValue));

      // Group fixed items by their group property
      const fixedItemsWithGroups = (this.fixedItems ?? []).filter(fi => fi.group != null);
      const fixedItemsByGroup = groupBy(fixedItemsWithGroups, i => i?.group);

      // Group regular items
      const groupedItems = groupBy(filteredItems, i => i?.group);

      // Get all unique group labels from both regular and fixed items
      const allGroupLabels = new Set([...groupedItems.keys(), ...fixedItemsByGroup.keys()]);

      // Merge fixed items with groups into their respective groups
      const groups: SelectGroup[] = Array.from(allGroupLabels)
        .map(label => {
          const groupFixedItems = this.sortItems(fixedItemsByGroup.get(label) ?? []);
          const regularItems = this.sortItems(groupedItems.get(label) ?? []);
          // Fixed items come first, then regular items (no mixing in sort)
          const allGroupItems = [...groupFixedItems, ...regularItems];
          return {
            label,
            items: allGroupItems,
            fixedItems: groupFixedItems,
          };
        })
        .sort((a, b) => a.label?.localeCompare(b.label) || 0);

      this.groups$.next(groups);
    } catch (error) {
      console.error('SelectWithSearchComponent filterItems failed:', error);
      // Provide safe fallback
      this.groups$.next([]);
    }
  }

  private updateSelection(selection: Selection, syncFixedItems: boolean): void {
    if (syncFixedItems) {
      selection = this.syncFixedItems(selection);
    }

    this._selectionModel = selection;
    this.updateSelectTriggerLabel();
    this.cdr.markForCheck();
  }

  private syncFixedItems(selection: Selection): Selection {
    if (!selection || !this.items || !this.multiple) {
      return selection;
    }

    let selectedItems = Array.isArray(selection) ? selection : [selection];

    // Collect all "Select All" type items (default selectAllItem + fixed items)
    const selectAllTypeItems = this.getSelectAllTypeItems();

    // Sync each "Select All" type item
    selectAllTypeItems.forEach(selectAllItem => {
      const allRegularItemsSelected = this.areAllRegularItemsSelected(selectedItems);
      const isSelectAllSelected = selectedItems.some(si => si.id === selectAllItem.id);

      if (allRegularItemsSelected && !isSelectAllSelected) {
        selectedItems.push(selectAllItem);
      } else if (!allRegularItemsSelected && isSelectAllSelected) {
        selectedItems = selectedItems.filter(i => i.id !== selectAllItem.id);
      }
    });

    return selectedItems;
  }

  private getSelectAllTypeItems(): SelectItem[] {
    const items: SelectItem[] = [];

    if (this.selectAllItem) {
      items.push(this.selectAllItem);
    }

    if (this.fixedItems?.length) {
      const fixedSelectAllItems = this.fixedItems.filter(fi => fi.type === SelectItemType.All);
      items.push(...fixedSelectAllItems);
    }

    return items;
  }

  private areAllRegularItemsSelected(selectedItems: SelectItem[]): boolean {
    return this.items.length > 0 && this.items.every(item =>
      selectedItems.some(selected => selected.id === item.id),
    );
  }

  onSelectAllClick(selected: boolean): void {
    if (!this.multiple || !this.selectAllItem) {
      return;
    }

    const explicitItems = this.getExplicitItems();

    if (selected) {
      this.updateSelection([this.selectAllItem, ...explicitItems, ...this.items], false);
    } else {
      this.updateSelection(explicitItems, false);
    }

    this.onChange(this.getSelectionIds());
  }

  onFixedItemClick(item: SelectItem, selected: boolean): void {
    if (!this.multiple) {
      return;
    }

    if (item.type === SelectItemType.All) {
      const explicitItems = this.getExplicitItems();

      if (selected) {
        this.updateSelection([item, ...explicitItems, ...this.items], false);
      } else {
        this.updateSelection(explicitItems, false);
      }

      this.onChange(this.getSelectionIds());
    } else if (item.type === SelectItemType.Explicit) {
      this.updateSelection(this.selectionModel, true);
      this.onChange(this.getSelectionIds());
    }
  }

  private getExplicitItems(): SelectItem[] {
    const selection = Array.isArray(this.selectionModel) ? this.selectionModel : [];
    return selection.filter(i => i?.type === SelectItemType.Explicit);
  }

  onItemClick(): void {
    if (!this.multiple) {
      return;
    }

    // Use setTimeout to allow mat-select to finish updating selectionModel first
    setTimeout(() => {
      const currentSelection = Array.isArray(this.selectionModel) ? [...this.selectionModel] : this.selectionModel;
      const syncedSelection = this.syncFixedItems(this.selectionModel);

      if (this.hasSelectionChanged(currentSelection, syncedSelection)) {
        this._selectionModel = syncedSelection;
        this.updateSelectTriggerLabel();

        if (this.matSelect) {
          this.matSelect.writeValue(syncedSelection);
        }

        this.cdr.markForCheck();
      }

      this.onChange(this.getSelectionIds());
    }, 0);
  }

  private hasSelectionChanged(current: Selection, synced: Selection): boolean {
    const currentIds = this.getIdsFromSelection(current).sort();
    const syncedIds = this.getIdsFromSelection(synced).sort();
    return currentIds.length !== syncedIds.length || !currentIds.every((id, index) => id === syncedIds[index]);
  }

  private getIdsFromSelection(selection: Selection): string[] {
    if (!selection) {
      return [];
    }
    const items = Array.isArray(selection) ? selection : [selection];
    return items.map(i => i?.id).filter(Boolean);
  }

  // ControlValueAccessor implementation
  writeValue(value: string | string[]): void {
    const selectionFromIds = this.getSelectionFromIds(value);
    this.updateSelection(selectionFromIds, true);
    this.cdr.markForCheck();
  }

  registerOnChange(onChange: (value: string | string[]) => void): void {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: () => void): void {
    this.onTouched = onTouched;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.markForCheck();
  }

  private notifyTouched(): void {
    this.onTouched?.();
  }

  private getSelectionIds(): string | string[] {
    if (!this._selectionModel) {
      return this.multiple ? [] : null;
    }

    if (Array.isArray(this._selectionModel)) {
      // Filter out the select all item from the returned selection
      const filteredItems = this._selectionModel.filter(item => item.id !== SelectWithSearchComponent.SELECT_ALL_ID);
      return filteredItems.map(item => item.id);
    } else {
      return this._selectionModel.id;
    }
  }

  private getSelectionFromIds(ids: string | string[]): Selection {
    if (ids == null) {
      return this.multiple ? [] : null;
    }

    // Handle case where items might be null/undefined
    const items = this.items || [];
    const fixedItems = this.fixedItems || [];
    const allItems = [...items, ...fixedItems];

    if (allItems.length === 0) {
      return this.multiple ? [] : null;
    }

    if (Array.isArray(ids)) {
      const selectedItems = allItems.filter(item => item && ids.includes(item.id));

      // Check if all regular items are selected and add selectAllItem if needed
      if (this.selectAllItem && this.multiple) {
        const regularItems = items.filter(item => item.type !== SelectItemType.All);
        const allRegularItemsSelected = regularItems.length > 0 && regularItems.every(item =>
          selectedItems.some(selected => selected.id === item.id),
        );

        if (allRegularItemsSelected) {
          // Add select all item at the beginning
          return [this.selectAllItem, ...selectedItems];
        }
      }

      return selectedItems;
    } else {
      return allItems.find(item => item && item.id === ids) || null;
    }
  }
}
